import React, { useState, useRef, useCallback } from 'react';
import { ProductCard } from './ProductCard';
import './ProductGrid.css';

export function ProductGrid({ products, onProductClick, loading = false, emptyMessage = "No products found", admin = false, draggable = false, onReorder, orderBaseIndex = 0 }) {
  const [draggedProduct, setDraggedProduct] = useState(null);
  const [dragOverProduct, setDragOverProduct] = useState(null);
  const didDragRef = useRef(false);

  const handleDragStart = useCallback((e, product) => {
    setDraggedProduct(product);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, product) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverProduct(product);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverProduct(null);
  }, []);

  const handleDrop = useCallback((e, targetProduct) => {
    e.preventDefault();
    setDragOverProduct(null);

    if (!draggedProduct || !targetProduct || draggedProduct.id === targetProduct.id) {
      setDraggedProduct(null);
      return;
    }

    if (!onReorder || !products) {
      setDraggedProduct(null);
      return;
    }

    // Use displayed position (index + base) as the order value - swap positions
    const draggedIndex = products.findIndex(p => p.id === draggedProduct.id);
    const targetIndex = products.findIndex(p => p.id === targetProduct.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedProduct(null);
      return;
    }

    const draggedOrder = orderBaseIndex + draggedIndex;
    const targetOrder = orderBaseIndex + targetIndex;

    const swaps = [
      { productId: draggedProduct.id, featuredOrder: targetOrder },
      { productId: targetProduct.id, featuredOrder: draggedOrder },
    ];

    didDragRef.current = true;
    onReorder(swaps);
    setDraggedProduct(null);
  }, [draggedProduct, products, onReorder, orderBaseIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedProduct(null);
    setDragOverProduct(null);
  }, []);

  const wrappedOnProductClick = useCallback((product) => {
    if (draggable && didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (onProductClick) {
      onProductClick(product);
    }
  }, [draggable, onProductClick]);

  if (loading) {
    return (
      <div className="product-grid product-grid--loading">
        <div className="product-grid-loading">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="product-grid">
        <div className="product-grid-empty">
          <i className="fa-solid fa-shopping-bag"></i>
          <h3>No products found</h3>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-grid">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onProductClick={wrappedOnProductClick}
          admin={admin}
          draggable={draggable}
          isDragging={draggedProduct?.id === product.id}
          isDragOver={dragOverProduct?.id === product.id}
          onDragStart={(e) => handleDragStart(e, product)}
          onDragOver={(e) => handleDragOver(e, product)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, product)}
          onDragEnd={handleDragEnd}
        />
      ))}
    </div>
  );
}
